import React, {useMemo } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { schoolProfile } from '../../common/assets';
import cdsConfig from '../styles/cds.config';
import UserMenu from './UserMenu';
import NewsLetterNavLink from './NewsletterNav';
import { useFeatureIsOn } from '@growthbook/growthbook-react';

type Organization = {
  profileImage: string;
  name: string;
  id: string;
}

type Me = {
  id: string;
  email: string;
  name: string;
}

type AdminRelationships = {
  loading: boolean;
  data: {
    me: Me;
    org: Organization;
  };
}

type AppBarProps = {
  onSignOut: () => void;
  orgAdmin: AdminRelationships;
}

const StyledAppBar = styled.div`
  display: flex;
  align-items: center;
  background-color: white;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  box-shadow: ${cdsConfig.shadows.sm};
  height: 3.75rem;
  z-index: 2;
`;

const Container = styled.div`
  box-sizing: border-box;
  flex-shrink: 0;
  margin: auto;
  width: 100%;
  padding: ${cdsConfig.space.$00} ${cdsConfig.space.$04};

  grid-area: header;
  display: flex;
  margin: unset;
  align-items: stretch;
  justify-content: space-between;
  height: 100%;

  ${cdsConfig.media.sm} {
    max-width: 100%;
  }
  ${cdsConfig.media.md} {
    max-width: 100%;
  }
  ${cdsConfig.media.lg} {
    max-width: 100%;
  }
  ${cdsConfig.media.xxl} {
    max-width: 100%;
  }
`;

const MenuWrapper = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: center;
`;

const ImageBox = styled.div`
  box-sizing: border-box;
  width: 1.5rem;
  height: 1.5rem;
  margin-right: ${cdsConfig.space.$02};
  border-radius: ${cdsConfig.radii.sm};
  object-fit: cover;
`;

const Box = styled.div`
  box-sizing: border-box;
  margin-right: ${cdsConfig.space.$05};
`;

const OrgName = styled.span`
  font-size: ${cdsConfig.fontSizes['heading-mobile']};
  line-height: ${cdsConfig.lineHeights['heading-mobile']};
  font-weight: ${cdsConfig.fontWeights['heading-mobile']};
  color: ${cdsConfig.colors['text-dark']};

  ${cdsConfig.media.sm} {
    font-size: ${cdsConfig.fontSizes.heading};
    line-height: ${cdsConfig.lineHeights.heading};
    font-weight: ${cdsConfig.fontWeights.heading};
  }
`;

const NavBar = styled.div`
  height: 100%;
`;

const NavRoot = styled.div`
  box-sizing: border-box;
  display: grid;
  grid-auto-flow: column;
  align-items: stretch;
  height: 100%;
`;

const StyledNavLink = styled.span`
  height: 100%;
  position: relative;
  display: flex;
  font-size: ${cdsConfig.fontSizes[100]};
  line-height: 1;
  align-items: center;
  justify-content: center;
  color: ${cdsConfig.colors['text-nav-normal']};
  border-bottom: 3px solid transparent;
  cursor: pointer;
  padding: ${cdsConfig.space.$04} ${cdsConfig.space.$06};
`;

const AppBar = ({ orgAdmin, onSignOut }: AppBarProps) => {
  const school = useMemo(() => {
    return {
      id: orgAdmin?.data?.org?.id,
      name: orgAdmin?.data?.org?.name,
      profileImage: orgAdmin?.data?.org?.profileImage,
    };
  }, [orgAdmin]);

  const { sid } = useParams<{sid: string}>();
  const classroomMenuEnabled = useFeatureIsOn('org-classroom');

  const orgUrl = process.env.REACT_APP_STAGE === 'prod' ? 'https://organization.classting.com' : 'https://organization.classting.net';

  return (
    <StyledAppBar>
      <Container>
        <MenuWrapper>
          <ImageBox
            as="img"
            src={school && (school.profileImage || schoolProfile)}
            alt={`${school?.name} logo`}
          />
          <Box>
            <OrgName>
              {school?.name}
            </OrgName>
          </Box>
          <NavBar>
            <NavRoot>
              <a href={`${orgUrl}/${sid}/members`}>
                <StyledNavLink>구성원</StyledNavLink>
              </a>
              <a href={`${orgUrl}/${sid}/org-units`}>
                <StyledNavLink>조직</StyledNavLink>
              </a>
              {classroomMenuEnabled && <a href={`${orgUrl}/${sid}/classes`}>
                <StyledNavLink>클래스</StyledNavLink>
              </a>}
              <NewsLetterNavLink />
              <a href={`${orgUrl}/${sid}/message-service`}>
                <StyledNavLink>문자</StyledNavLink>
              </a>
              <a href={`${orgUrl}/${sid}/ai`}>
                <StyledNavLink>AI 러닝</StyledNavLink>
              </a>
            </NavRoot>
          </NavBar>
        </MenuWrapper>
        <UserMenu onSignOut={onSignOut} />
      </Container>
    </StyledAppBar>
  );
};

const mapStateToProps = (state: any) => ({
  orgAdmin: state.admin.adminRelationships,
});

export default connect(
  mapStateToProps,
)(AppBar);
